import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { complianceApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, 
  Download,
  FileText,
  Building2,
  Landmark,
  Receipt,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

export default function CompliancePage() {
  const { canAccessReports } = useAuth();
  const [compliance, setCompliance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      const [complianceRes, summaryRes] = await Promise.all([
        complianceApi.getAll({ month: selectedMonth, year: selectedYear }),
        complianceApi.getSummary(selectedMonth, selectedYear)
      ]);
      setCompliance(complianceRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  };

  const exportToCSV = (type) => {
    let headers, rows, filename;

    if (type === 'pf') {
      headers = ['Employee', 'PF (Employee)', 'PF (Employer)', 'Total'];
      rows = compliance.map(c => [
        c.employee_name,
        c.pf_employee,
        c.pf_employer,
        c.pf_employee + c.pf_employer
      ]);
      filename = `pf_report_${selectedMonth}_${selectedYear}.csv`;
    } else if (type === 'esi') {
      headers = ['Employee', 'ESI (Employee)', 'ESI (Employer)', 'Total'];
      rows = compliance.map(c => [
        c.employee_name,
        c.esi_employee,
        c.esi_employer,
        c.esi_employee + c.esi_employer
      ]);
      filename = `esi_report_${selectedMonth}_${selectedYear}.csv`;
    } else if (type === 'pt') {
      headers = ['Employee', 'Professional Tax'];
      rows = compliance.map(c => [c.employee_name, c.professional_tax]);
      filename = `pt_report_${selectedMonth}_${selectedYear}.csv`;
    } else if (type === 'tds') {
      headers = ['Employee', 'TDS'];
      rows = compliance.map(c => [c.employee_name, c.tds]);
      filename = `tds_report_${selectedMonth}_${selectedYear}.csv`;
    }

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Statutory Compliance</h1>
          <p className="page-description">PF, ESI, Professional Tax & TDS Management</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(month => (
                  <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Provident Fund</p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_pf_employee + summary.total_pf_employer)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Employee: {formatCurrency(summary.total_pf_employee)} | Employer: {formatCurrency(summary.total_pf_employer)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Landmark className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">ESI</p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_esi_employee + summary.total_esi_employer)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Employee: {formatCurrency(summary.total_esi_employee)} | Employer: {formatCurrency(summary.total_esi_employer)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Professional Tax</p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_professional_tax)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tamil Nadu PT
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Receipt className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">TDS</p>
                  <p className="text-xl font-bold">{formatCurrency(summary.total_tds)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Income Tax
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#D4A017]/20 flex items-center justify-center">
                  <Calculator className="h-6 w-6 text-[#D4A017]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Tabs */}
      <Tabs defaultValue="pf" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pf">Provident Fund</TabsTrigger>
          <TabsTrigger value="esi">ESI</TabsTrigger>
          <TabsTrigger value="pt">Professional Tax</TabsTrigger>
          <TabsTrigger value="tds">TDS</TabsTrigger>
        </TabsList>

        {/* PF Tab */}
        <TabsContent value="pf">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Provident Fund Report</CardTitle>
                <CardDescription>Employee and employer PF contributions</CardDescription>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => exportToCSV('pf')}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ComplianceTable 
                data={compliance}
                loading={loading}
                columns={[
                  { key: 'employee_name', label: 'Employee' },
                  { key: 'pf_employee', label: 'Employee Contribution', format: 'currency' },
                  { key: 'pf_employer', label: 'Employer Contribution', format: 'currency' },
                  { key: 'pf_total', label: 'Total', format: 'currency', compute: (row) => row.pf_employee + row.pf_employer }
                ]}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ESI Tab */}
        <TabsContent value="esi">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>ESI Report</CardTitle>
                <CardDescription>Employee State Insurance contributions</CardDescription>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => exportToCSV('esi')}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ComplianceTable 
                data={compliance}
                loading={loading}
                columns={[
                  { key: 'employee_name', label: 'Employee' },
                  { key: 'esi_employee', label: 'Employee Contribution (0.75%)', format: 'currency' },
                  { key: 'esi_employer', label: 'Employer Contribution (3.25%)', format: 'currency' },
                  { key: 'esi_total', label: 'Total', format: 'currency', compute: (row) => row.esi_employee + row.esi_employer }
                ]}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Professional Tax Tab */}
        <TabsContent value="pt">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Professional Tax Report</CardTitle>
                <CardDescription>Tamil Nadu Professional Tax deductions</CardDescription>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => exportToCSV('pt')}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ComplianceTable 
                data={compliance}
                loading={loading}
                columns={[
                  { key: 'employee_name', label: 'Employee' },
                  { key: 'professional_tax', label: 'Professional Tax', format: 'currency' }
                ]}
                formatCurrency={formatCurrency}
              />
            </CardContent>
            <CardContent className="pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-2">Tamil Nadu Professional Tax Slabs:</p>
                <ul className="space-y-1">
                  <li>• Up to ₹21,000: Nil</li>
                  <li>• ₹21,001 - ₹30,000: ₹100</li>
                  <li>• ₹30,001 - ₹45,000: ₹235</li>
                  <li>• ₹45,001 - ₹60,000: ₹510</li>
                  <li>• ₹60,001 - ₹75,000: ₹760</li>
                  <li>• Above ₹75,000: ₹1,095</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TDS Tab */}
        <TabsContent value="tds">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>TDS Report</CardTitle>
                <CardDescription>Tax Deducted at Source</CardDescription>
              </div>
              <Button variant="outline" className="gap-2" onClick={() => exportToCSV('tds')}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ComplianceTable 
                data={compliance}
                loading={loading}
                columns={[
                  { key: 'employee_name', label: 'Employee' },
                  { key: 'tds', label: 'TDS Deducted', format: 'currency' }
                ]}
                formatCurrency={formatCurrency}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Compliance Table Component
function ComplianceTable({ data, loading, columns, formatCurrency }) {
  return (
    <div className="responsive-table-container">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(col => (
              <TableHead key={col.key} className={col.format === 'currency' ? 'text-right' : ''}>
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                {columns.map((_, j) => (
                  <TableCell key={j}>
                    <div className="h-4 bg-muted rounded animate-pulse" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                No compliance data found for this period
              </TableCell>
            </TableRow>
          ) : (
            data.map((row) => (
              <TableRow key={row.id}>
                {columns.map(col => (
                  <TableCell 
                    key={col.key} 
                    className={col.format === 'currency' ? 'text-right font-medium' : ''}
                  >
                    {col.compute 
                      ? formatCurrency(col.compute(row))
                      : col.format === 'currency' 
                        ? formatCurrency(row[col.key])
                        : row[col.key]
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
